import { useState } from 'react';
import type { CalendarInfo } from 'zitimail-shared-types';

import { calendar } from '../api/calendar';
import { dayKey, timeLabel } from './dates';

/**
 * The small form that opens where you drew or clicked: a title is enough.
 * Anything more — guests, repeat, notes — is one click away in the full form.
 */
export function QuickCreate({
  start,
  end,
  allDay,
  x,
  y,
  calendars,
  onClose,
  onSaved,
  onMore,
}: {
  start: Date;
  end: Date;
  allDay: boolean;
  x: number;
  y: number;
  calendars: readonly CalendarInfo[];
  onClose: () => void;
  onSaved: () => void;
  onMore: (title: string) => void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [target, setTarget] = useState(calendars[0]?.href ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Beside the pointer, kept on screen.
  const left = Math.max(12, Math.min(x + 12, window.innerWidth - 332));
  const top = Math.max(12, Math.min(y - 40, window.innerHeight - 220));
  const when = allDay
    ? start.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })
    : `${start.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}, ${timeLabel(start.toISOString())} – ${timeLabel(end.toISOString())}`;

  return (
    <div className="popover-scrim" role="presentation" onClick={onClose}>
      <form
        className="quick-create"
        role="dialog"
        aria-label="New event"
        style={{ left, top }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim()) {
            setError('Give it a title.');
            return;
          }
          setSaving(true);
          calendar
            .create({
              calendar: target,
              title: title.trim(),
              start: allDay ? `${dayKey(start)}T00:00:00.000Z` : start.toISOString(),
              end: allDay ? `${dayKey(end)}T00:00:00.000Z` : end.toISOString(),
              allDay,
              repeat: 'none',
              attendees: [],
            })
            .then(onSaved)
            .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Not saved.'))
            .finally(() => setSaving(false));
        }}
      >
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add a title"
          aria-label="Title"
          autoFocus
        />
        <p className="qc-when">{when}</p>
        {calendars.length > 1 && (
          <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Calendar">
            {calendars.map((c) => (
              <option key={c.href} value={c.href}>
                {c.name}
              </option>
            ))}
          </select>
        )}
        {error && (
          <span className="adm-error" role="alert">
            {error}
          </span>
        )}
        <div className="qc-actions">
          <button type="button" className="link-btn" onClick={() => onMore(title.trim())}>
            More options
          </button>
          <button type="submit" className="btn" style={{ width: 'auto' }} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
